import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq } from "./cityPricing";

const antibes: ChauffeurPage = {
  slug: "antibes",
  title: "VTC & chauffeur privé à Antibes / Juan-les-Pins",
  subtitle:
    "Chauffeur privé à Antibes : Port Vauban et ses superyachts, Cap d'Antibes, Juan-les-Pins et Jazz à Juan.",
  keywords: [
    "vtc antibes",
    "chauffeur privé antibes",
    "chauffeur juan-les-pins",
    "chauffeur port vauban",
  ],
  hero: {
    title: "VTC & chauffeur privé à Antibes / Juan-les-Pins",
    subtitle:
      "Port Vauban, Cap d'Antibes et Juan-les-Pins : un chauffeur pour équipages, propriétaires et invités, 24h/24.",
    keywords: [
      "VTC Antibes",
      "chauffeur Port Vauban",
      "chauffeur Juan-les-Pins",
      "transfert aéroport Nice Antibes",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & chauffeur privé à Antibes",
    badges: ["Yachting Port Vauban", "Cap d'Antibes", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Antibes",
    items: [
      {
        title: "Au service du yachting",
        description:
          "Port Vauban, plus grande marina d'Europe : transferts équipages, propriétaires et invités.",
      },
      {
        title: "Cap d'Antibes",
        description:
          "Villas et hôtels du Cap : dépose discrète sur des adresses privées et isolées.",
      },
      {
        title: "Juan-les-Pins",
        description:
          "Plages, festival Jazz à Juan et vie nocturne : navettes et retours de soirée.",
      },
      {
        title: "Aéroport tout proche",
        description:
          "Liaison rapide vers Nice Côte d'Azur, idéale pour rotations d'équipage et arrivées invités.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur à Antibes",
    services: [
      {
        title: "Transferts yachting",
        text: "Port Vauban et quai des Milliardaires : équipages, provisioning et invités, horaires souples.",
      },
      {
        title: "Transferts aéroport",
        text: "Antibes ↔ aéroport de Nice avec suivi de vol, pour arrivées et départs d'invités.",
      },
      {
        title: "Mise à disposition",
        text: "Chauffeur dédié pour propriétaires et familles, du Cap d'Antibes à toute la Côte.",
      },
      {
        title: "Soirées Juan-les-Pins",
        text: "Plages privées, casino et concerts de Jazz à Juan, retour de nuit en sécurité.",
      },
      {
        title: "Vieil Antibes",
        text: "Marché Provençal, remparts et ruelles : dépose au plus près malgré le piétonnier.",
      },
    ],
  },
  locations: {
    heading: "Quartiers & points de prise en charge",
    items: [
      {
        name: "Port Vauban",
        description: "Plus grande marina d'Europe, quai des Milliardaires.",
      },
      {
        name: "Cap d'Antibes",
        description: "Villas privées, Eden-Roc, Garoupe.",
      },
      {
        name: "Juan-les-Pins",
        description: "Plages, Pinède Gould, vie nocturne.",
      },
      {
        name: "Vieil Antibes",
        description: "Remparts, marché Provençal, musée Picasso.",
      },
      {
        name: "Marina Baie des Anges",
        description: "Résidences et port de plaisance à Villeneuve-Loubet.",
      },
      {
        name: "Sophia Antipolis",
        description: "Technopôle voisine pour déplacements business.",
      },
    ],
  },
  generic: {
    heading: "Antibes, capitale du yachting de la Riviera",
    subheading:
      "Port Vauban, Cap d'Antibes et Juan-les-Pins : trois logiques, un chauffeur.",
    paragraphs: [
      "Port Vauban est la plus grande marina d'Europe et son quai des Milliardaires accueille les plus grands yachts. Équipages, provisioning, rotations d'invités : nous gérons des horaires mouvants et des points de prise en charge précis au pied des pontons.",
      "Le Cap d'Antibes aligne des villas et des hôtels d'exception sur des routes étroites et confidentielles. Un chauffeur qui en connaît les accès dépose à la grille, sans hésitation ni manœuvre approximative.",
      "Côté Juan-les-Pins, la saison estivale et le festival Jazz à Juan concentrent plages privées, concerts et sorties tardives. La mise à disposition assure des retours sereins, sans contrainte de stationnement ni d'alcool au volant.",
    ],
    bullets: ["Horaires équipage souples", "Dépose villa Cap", "Suivi de vol"],
  },
  process: {
    heading: "Réserver un chauffeur à Antibes",
    steps: [
      "Indiquez le point de prise en charge (ponton Port Vauban, villa du Cap, hôtel, aéroport).",
      "Nous calons l'heure selon l'arrivée du yacht, du vol ou de l'événement.",
      "Le chauffeur se présente au plus près du quai ou de l'adresse privée.",
      "Transfert direct ou mise à disposition pour la journée selon vos besoins.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Antibes"),
    {
      q: "Assurez-vous les transferts depuis Port Vauban et le quai des Milliardaires ?",
      a: "Oui. Nous prenons en charge équipages, propriétaires et invités au plus près des pontons, avec des horaires adaptés aux arrivées et départs de yachts.",
    },
    {
      q: "Combien de temps entre Antibes et l'aéroport de Nice ?",
      a: "Comptez environ 30 à 40 minutes par l'A8 hors heures de pointe. Nous suivons le vol des invités pour ajuster la prise en charge.",
    },
    {
      q: "Pouvez-vous desservir les villas du Cap d'Antibes ?",
      a: "Oui, nous assurons la dépose et la reprise directement à l'adresse de la villa ou de l'hôtel (Eden-Roc, Garoupe…), en toute discrétion et en coordination avec la conciergerie si besoin.",
    },
    {
      q: "Gérez-vous les retours tardifs pendant Jazz à Juan ?",
      a: "Oui, via la mise à disposition : le chauffeur reste disponible pour un retour de nuit après un concert de Jazz à Juan ou une soirée en plage privée.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, un atout pour les équipages et les propriétaires internationaux du Port Vauban.",
    },
    {
      q: "Quels véhicules proposez-vous à Antibes ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places pour les équipages et les bagages, SUV et minibus VIP selon l'effectif et l'occasion.",
    },
    {
      q: "Faites-vous les longues distances depuis Antibes ?",
      a: "Oui, trajets toute distance : Monaco, Saint-Tropez, Cannes, l'Italie voisine (Vintimille, Sanremo) et les stations de ski, avec un chauffeur dédié.",
    },
  ],
  relatedLinks: [
    {
      label: "Transfert Cannes ⇄ Aéroport de Nice",
      href: "/chauffeur/transferts/cannes-aeroport-nice",
    },
    {
      label: "Transfert Aéroport de Nice ⇄ Saint-Jean-Cap-Ferrat",
      href: "/chauffeur/transferts/aeroport-nice-saint-jean-cap-ferrat",
    },
    {
      label: "Van avec chauffeur (équipages & bagages)",
      href: "/chauffeur/van-avec-chauffeur",
    },
    {
      label: "Location de véhicule à Antibes",
      href: "/location/location-voiture-antibes",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Antibes",
    text: "Yachting à Port Vauban, villa au Cap ou soirée à Juan-les-Pins : réservez votre chauffeur.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur privé à Antibes 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Antibes / Juan-les-Pins : Port Vauban, Cap d'Antibes, transfert aéroport de Nice et Jazz à Juan. 5,0/5 sur 38 avis. Devis immédiat.",
    canonical: "/chauffeur/antibes",
  },
};

export default antibes;
